import { useMemo } from "react";
import { translate } from "app/filters/i18nFilter";
import { useIsCompanyAsLeadClaimEnabled } from "./useIsCompanyAsLeadClaimEnabled";
import type { ExportName } from "app/pages/sales-intelligence/sub-modules/management/store/type";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";
import {
    MISSING_FIELDS_IN_ACCOUNTS,
    MISSING_FIELDS_IN_CONTACTS,
    MISSING_FIELDS_IN_LEADS,
} from "../../management/translations/integrationsKeys";

export const useMissingFieldsWarning = ({
    missingRequiredFieldsCount,
    integrationName,
}: {
    missingRequiredFieldsCount: Record<ExportName, number>;
    integrationName: Integration;
}) => {
    const isCompanyAsLeadExportClaimEnabled = useIsCompanyAsLeadClaimEnabled(integrationName);

    const missingFieldsCounts = useMemo(() => {
        const missingResult = [];

        if (missingRequiredFieldsCount.accounts > 0) {
            const missingFieldsInAccounts = translate(MISSING_FIELDS_IN_ACCOUNTS[integrationName], {
                count: missingRequiredFieldsCount.accounts,
            });

            missingResult.push(missingFieldsInAccounts);
        }

        if (missingRequiredFieldsCount.contacts > 0) {
            const missingFieldsInContacts = translate(MISSING_FIELDS_IN_CONTACTS, {
                count: missingRequiredFieldsCount.contacts,
            });

            missingResult.push(missingFieldsInContacts);
        }

        if (missingRequiredFieldsCount.leads > 0) {
            const missingFieldsInLeads = translate(MISSING_FIELDS_IN_LEADS, {
                count: missingRequiredFieldsCount.leads,
            });

            missingResult.push(missingFieldsInLeads);
        }

        if (isCompanyAsLeadExportClaimEnabled && missingRequiredFieldsCount.companyAsLeads > 0) {
            const missingFieldsInCompanyAsLeads = translate(MISSING_FIELDS_IN_LEADS, {
                count: missingRequiredFieldsCount.companyAsLeads,
            });

            missingResult.push(missingFieldsInCompanyAsLeads);
        }

        return missingResult;
    }, [missingRequiredFieldsCount, isCompanyAsLeadExportClaimEnabled, integrationName]);

    return missingFieldsCounts;
};
